import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bell, MessageSquare, ShoppingBag, Gift, Info } from 'lucide-react'
import { getNotifications, markAsRead, markAllAsRead } from '../../../services/notificationService'
import { useUnreadNotifications } from '../../../hooks/useUnreadNotifications'
import BrandedLoader from '../../../components/ui/BrandedLoader'

export default function Notifications() {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const { refresh: refreshUnread } = useUnreadNotifications({ role: 'user' })

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        setLoading(true)
        const { data } = await getNotifications()
        setNotifications(data.data || [])
      } catch (err) {
        console.error('Failed to fetch notifications', err)
      } finally {
        setLoading(false)
      }
    }
    fetchNotifs()
    const timer = window.setInterval(fetchNotifs, 10000)
    return () => window.clearInterval(timer)
  }, [])

  const openNotification = async (notif) => {
    try {
      if (!notif.is_read) {
        await markAsRead(notif.id)
        setNotifications((prev) =>
          prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)),
        )
        refreshUnread()
      }
    } catch (err) {
      console.error(err)
    }

    const path = notif?.data?.path
    if (path) {
      navigate(path)
      return
    }

    if (notif.type === 'chat' && notif?.data?.order_id) {
      navigate(`/app/order/${notif.data.order_id}/chat`)
    }
  }

  const getIcon = (type) => {
    if (type === 'chat') return <MessageSquare size={20} className="text-[#4F46E5]" />
    if (type === 'order') return <ShoppingBag size={20} className="text-[#4F46E5]" />
    if (type === 'promo') return <Gift size={20} className="text-orange-500" />
    if (type === 'system') return <Info size={20} className="text-blue-500" />
    return <Bell size={20} className="text-gray-500" />
  }

  const getBgColor = (type) => {
    if (type === 'chat' || type === 'order') return 'bg-indigo-50'
    if (type === 'promo') return 'bg-orange-50'
    if (type === 'system') return 'bg-blue-50'
    return 'bg-gray-50'
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#FAFAFA] pb-24 md:pb-12">
      <div className="flex items-center justify-between px-6 pt-app pb-4 bg-[#FAFAFA] sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1 -ml-1 text-gray-900 rounded-full transition-colors hover:bg-gray-100">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
        </div>
        {notifications.some((n) => !n.is_read) ? (
          <button
            type="button"
            onClick={async () => {
              try {
                await markAllAsRead()
                setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
                refreshUnread()
              } catch (err) {
                console.error(err)
              }
            }}
            className="text-xs font-bold text-[#4F46E5]"
          >
            Mark all read
          </button>
        ) : null}
      </div>

      <div className="px-6 w-full max-w-md mx-auto pt-4 flex-1">
        {loading ? (
          <BrandedLoader size="md" />
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Bell size={32} className="text-gray-300" />
            </div>
            <h3 className="text-[17px] font-bold text-gray-900 mb-2">No notifications yet</h3>
            <p className="text-[14px] text-gray-500 max-w-[240px]">When you get updates about your orders or promotions, they'll show up here.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {notifications.map((notif) => (
              <button
                key={notif.id}
                type="button"
                onClick={() => openNotification(notif)}
                className={`bg-white rounded-2xl p-4 shadow-sm border flex gap-4 text-left cursor-pointer transition-colors ${
                  notif.is_read
                    ? 'border-gray-100 opacity-70'
                    : 'border-[#4F46E5]/20 hover:border-[#4F46E5]/40'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${getBgColor(notif.type)}`}>
                  {getIcon(notif.type)}
                </div>
                <div className="flex-1 pt-1">
                  <div className="flex justify-between items-start mb-1">
                    <h4
                      className={`text-[14px] ${
                        notif.is_read ? 'font-semibold text-gray-700' : 'font-bold text-gray-900'
                      }`}
                    >
                      {notif.title}
                    </h4>
                    <span className="text-[10px] text-gray-400 shrink-0 mt-0.5">
                      {notif.created_at ? new Date(notif.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                  <p
                    className={`text-[12px] leading-relaxed ${
                      notif.is_read ? 'text-gray-500' : 'text-gray-600'
                    }`}
                  >
                    {notif.message}
                  </p>
                </div>
                {!notif.is_read && (
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0 shadow-sm" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
